import { action, makeObservable } from "mobx";
import { MessageType } from "src/components/notifications/Notifier";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { GetFileInfoById, GetFileInfoById_file, GetFileInfoByIdVariables } from "src/api/generated/GetFileInfoById";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_FILE_INFO_BY_ID } from "src/api/cred";
import Axios from "src/network/Axios";
import { AxiosResponse } from "axios";

export class FileStore {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            downloadFile: action,
            downloadFileAndTriggerDownload: action,
            getFileInfo: action,
            triggerDownload: action
        });
    }

    downloadFile = async (fileId: string): Promise<AxiosResponse<Blob> | undefined> => {
        let downloadedInvoiceFile = undefined;

        try {
            const role = this.rootStore.authStore.user?.role;

            const downloadInvoiceFileResponse = await Axios.getAuthInstance().get(
                `${NetworkConfig.downloadFileVersion2}/${fileId}`,
                {
                    urlWithoutParams: NetworkConfig.downloadFileVersion2,
                    responseType: "blob",
                    headers: {
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            downloadedInvoiceFile = downloadInvoiceFileResponse;
        } catch (error) {
            console.error("Error downloading file: ", error);
        }

        return downloadedInvoiceFile;
    };

    downloadFileAndTriggerDownload = async (fileId: string, fileName?: string) => {
        const downloadedFile = await this.downloadFile(fileId);
        const defaultFileName = i18next.t("labels.file");

        if (downloadedFile) {
            try {
                let fileBlob = downloadedFile.data;
                const objUrl = window.URL.createObjectURL(fileBlob);
                const fileInfo = await this.getFileInfo(fileId);
                // Trigger the download
                const a = document.createElement("a");
                a.href = objUrl;
                a.download = fileName ? fileName : fileInfo ? fileInfo.name : defaultFileName;

                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (error) {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("stores.file_store.error_downloading_file"),
                    MessageType.ERROR
                );
            }
        }
    };

    getFileInfo = async (fileId: string): Promise<GetFileInfoById_file | undefined> => {
        let fileInfo: GetFileInfoById_file | undefined = undefined;

        const { data }: { data: GetFileInfoById } = await apolloClientInstance.query<
            GetFileInfoById,
            GetFileInfoByIdVariables
        >({
            query: GET_FILE_INFO_BY_ID,
            variables: {
                fileid: fileId
            }
        });

        if (data.file.length > 0) {
            const file = data.file[0];
            fileInfo = file;
        }

        return fileInfo;
    };

    triggerDownload = async (fileBlob: Blob, fileName?: string) => {
        const defaultFileName = i18next.t("labels.file");

        try {
            const objUrl = window.URL.createObjectURL(fileBlob);
            // Trigger the download
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = fileName ?? defaultFileName;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(objUrl);
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("stores.file_store.error_downloading_file"),
                MessageType.ERROR
            );
        }
    };
}
